import { api, ApiError } from "./apiClient";

export interface DevRolePreset {
  id: string;
  label: string;
  groups: string[];
}

export interface DevRoleSwitchState {
  enabled: boolean;
  active: boolean;
  groups: string[];
  groups_real: string[];
  can_manage: boolean;
  presets: DevRolePreset[];
  available_groups: string[];
  dev_preserve_groups?: string[];
  preset: string | null;
  impersonate_login?: string | null;
}

export interface DevRoleCatalog {
  employees: DevRoleEmployeeSearchHit[];
  groups: string[];
  employee_count: number;
  dev_preserve_groups: string[];
}

export interface DevRoleEmployeeSearchHit {
  id: string;
  login: string;
  label: string;
  email?: string | null;
  subtitle?: string | null;
  groups: string[];
}

const EMPLOYEE_GUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Anzeigename als Hauptzeile, E-Mail darunter. Objekt-IDs bleiben unsichtbar. */
export function employeeOptionText(row: DevRoleEmployeeSearchHit): {
  label: string;
  description?: string;
} {
  const email = (row.email || row.subtitle || "").trim();
  const rawName = (row.label || "").trim();
  const name = rawName && !EMPLOYEE_GUID_RE.test(rawName) ? rawName : "";
  const mail = email && !EMPLOYEE_GUID_RE.test(email) ? email : "";
  const label = name || mail || rawName || row.login;
  const description =
    mail && mail.localeCompare(label, "de", { sensitivity: "accent" }) !== 0 ? mail : undefined;
  return { label, description };
}

/** True, wenn fast jede Zeile nur eine Entra-Objekt-ID ist. */
export function employeeListHidesNames(labels: string[]): boolean {
  const present = labels.map((label) => label.trim()).filter(Boolean);
  if (present.length < 2) return false;
  const unresolved = present.filter((label) => EMPLOYEE_GUID_RE.test(label)).length;
  return unresolved >= present.length - 1;
}

export type DevRoleSwitchFetchResult =
  | { ok: true; state: DevRoleSwitchState }
  | { ok: false; status?: number; message: string };

export async function fetchDevRoleSwitchState(): Promise<DevRoleSwitchFetchResult> {
  try {
    const data = await api.get<DevRoleSwitchState>("/dev/role-switch");
    if (!data?.enabled) {
      return {
        ok: false,
        message: "Rollenwechsel auf dieser API-Instanz deaktiviert (DEV_ROLE_SWITCH_ENABLED prüfen).",
      };
    }
    if (!data.can_manage) {
      return { ok: false, message: "Nur Admins dürfen Rollen simulieren." };
    }
    return { ok: true, state: data };
  } catch (err) {
    const status = err instanceof ApiError ? err.status : undefined;
    if (status === 404) {
      return {
        ok: false,
        status: 404,
        message:
          "Rollenwechsel hier deaktiviert oder Endpunkt fehlt (DEV_ROLE_SWITCH_ENABLED / Deploy prüfen).",
      };
    }
    if (status === 401) {
      return { ok: false, status: 401, message: "Nicht angemeldet (Session-Cookie fehlt)." };
    }
    return {
      ok: false,
      status,
      message:
        status != null
          ? `API antwortet mit HTTP ${status}.`
          : "API nicht erreichbar (Netzwerk/CORS).",
    };
  }
}

export async function fetchDevRoleCatalog(q = ""): Promise<DevRoleCatalog> {
  const query = q.trim() ? `?q=${encodeURIComponent(q.trim())}` : "";
  return api.get<DevRoleCatalog>(`/dev/role-switch/catalog${query}`);
}

export async function searchDevRoleEmployees(
  q: string,
  limit = 500,
): Promise<DevRoleEmployeeSearchHit[]> {
  const params = new URLSearchParams();
  if (q.trim()) params.set("q", q.trim());
  params.set("limit", String(limit));
  const list = await api.get<DevRoleEmployeeSearchHit[]>(
    `/dev/role-switch/employees/search?${params.toString()}`,
  );
  return Array.isArray(list) ? list : [];
}

export async function fetchDevRoleUserGroups(
  login: string,
  email?: string | null,
): Promise<string[]> {
  const params = new URLSearchParams();
  const mail = (email || "").trim();
  if (mail) params.set("email", mail);
  const query = params.toString();
  const data = await api.get<{ groups: string[] }>(
    `/dev/role-switch/users/${encodeURIComponent(login)}/groups${query ? `?${query}` : ""}`,
  );
  return Array.isArray(data?.groups) ? data.groups : [];
}

export async function applyDevRoleGroups(groups: string[]): Promise<void> {
  await api.put("/dev/role-switch", { groups });
}

export async function applyDevRoleImpersonation(
  login: string,
  email?: string | null,
): Promise<void> {
  await api.put("/dev/role-switch", {
    impersonate_login: login,
    impersonate_email: email || undefined,
  });
}

export async function clearDevRoleSwitch(): Promise<void> {
  await api.del("/dev/role-switch");
}
